import React from 'react';
import type { NextPage, GetStaticProps, GetStaticPaths } from 'next';
import { G } from '@mobily/ts-belt';
import { serverLogger } from '../../utils/loggers/server';
import { type DynamicRoutePath } from '../../utils/requests/contentstack/dynamicPath';
import { SlugPageLayout } from '../../components/SlugPageLayout/SlugPageLayout';
import {
  getSlugEntry,
  GetSlugEntryResponse,
  getSlugPaths,
} from '../../utils/requests/contentstack/slug';

type Params = DynamicRoutePath['params'];

const rootPath = '/all';

export const getStaticPaths: GetStaticPaths<Params> = async ({ locales }) => {
  const paths = await getSlugPaths({
    locales,
    logger: serverLogger,
    rootPath,
  });

  // Setting `fallback` to false to properly 404 on any page not statically generated
  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps<
  GetSlugEntryResponse,
  Params
> = async ({ params, locale }) => {
  if (!params) {
    return {
      notFound: true,
    };
  }

  const pageUrl = `${rootPath}/${params.slug.join('/')}`;

  const entryResponse = await getSlugEntry({
    locale,
    logger: serverLogger,
    rootPath,
    slug: params.slug,
  });

  if (G.isUndefined(entryResponse)) {
    serverLogger({
      level: 'warn',
      team: 'client-onboarding',
      message: 'Required page data is undefined - /all/',
      context: {
        page: '/all/[...slug]',
        url: pageUrl,
      },
    });

    return {
      notFound: true,
    };
  }

  return {
    props: entryResponse,
    // revalidate configures the cache policy, this sets it to cache for 5 minutes
    // https://nextjs.org/docs/pages/building-your-application/deploying/production-checklist#caching
    revalidate: 300,
  };
};

const DynamicPage: NextPage<GetSlugEntryResponse> = props => {
  return <SlugPageLayout {...props} />;
};

export default DynamicPage;
